/**
 * Monitoring and error tracking utilities
 */

import React from 'react';
import { logger } from './logger';
import { mayTrack } from './trackingConsent';
import { appConfig } from '../config/appConfig';

interface ErrorInfo {
  message: string;
  stack?: string;
  componentStack?: string;
  timestamp: number;
  url: string;
  userAgent: string;
  userId?: string;
  sessionId: string;
  environment: string;
}

interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: number;
  url: string;
  environment: string;
}

class MonitoringService {
  private sessionId: string;
  private environment: string;
  private userId?: string;
  private errors: ErrorInfo[] = [];
  private metrics: PerformanceMetric[] = [];
  private maxStoredItems = 100;

  constructor() {
    this.sessionId = this.generateSessionId();
    // S135: this read the RAW variable and compared it with `===`, which is the
    // S128 defect a second time in a second file: "Production", "prod" or a
    // trailing space is not 'production', so on a district deploy with a
    // capitalised value every `environment === 'production'` branch below —
    // error reporting, analytics, performance metrics — silently did not run,
    // and the environment name stamped into every error report was the raw
    // string. `appConfig` recognises the name (and shouts when it cannot).
    this.environment = appConfig.getEnvironment();
    this.initializePerformanceObservers();
    this.setupGlobalErrorHandlers();
  }

  private generateSessionId(): string {
    // Telemetry correlation id — never used for auth or access.
    // eslint-disable-next-line no-restricted-syntax
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private initializePerformanceObservers(): void {
    if (typeof window === 'undefined' || !('PerformanceObserver' in window)) {
      return;
    }

    try {
      // Core Web Vitals - LCP
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1] as PerformanceEntry;
        this.recordMetric('LCP', lastEntry.startTime);
      });
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

      // Core Web Vitals - FID
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          const fidEntry = entry as PerformanceEntry & { processingStart: number };
          this.recordMetric('FID', fidEntry.processingStart - fidEntry.startTime);
        });
      });
      fidObserver.observe({ entryTypes: ['first-input'] });

      // Core Web Vitals - CLS
      const clsObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          const clsEntry = entry as PerformanceEntry & { hadRecentInput: boolean; value: number };
          if (!clsEntry.hadRecentInput) {
            this.recordMetric('CLS', clsEntry.value);
          }
        });
      });
      clsObserver.observe({ entryTypes: ['layout-shift'] });

    } catch (error) {
      console.warn('Performance observers not fully supported:', error);
    }
  }

  private setupGlobalErrorHandlers(): void {
    // Global error handler
    window.addEventListener('error', (event) => {
      this.logError(new Error(event.message), undefined, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
    });

    // Unhandled promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      this.logError(new Error(event.reason), undefined, {
        type: 'unhandledrejection'
      });
    });
  }

  setUserId(userId: string): void {
    this.userId = userId;
  }

  logError(
    error: Error,
    componentStack?: string,
    additionalInfo?: Record<string, unknown>
  ): void {
    const errorInfo: ErrorInfo = {
      message: error.message,
      stack: error.stack,
      componentStack,
      timestamp: Date.now(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      userId: this.userId,
      sessionId: this.sessionId,
      environment: this.environment
    };

    this.errors.push(errorInfo);

    // Keep only the most recent errors
    if (this.errors.length > this.maxStoredItems) {
      this.errors.shift();
    }

    // Log to console in development
    if (appConfig.isDevelopment()) {
      console.error('Monitored Error:', errorInfo, additionalInfo);
    }

    // S135: `process.env.X === 'true'` here disagreed with appConfig, which
    // FORCES this on in production — so an audit reading the config saw error
    // reporting enabled while this line kept it off whenever the variable was
    // unset. One flag, one reader.
    //
    // S136: and `isProduction()` was the other half of the same silence.
    // Staging is where a pilot runs, and an error there was neither forwarded
    // (not production) nor logged (not development): it went into an in-memory
    // array nothing reads and vanished with the tab. Forward wherever the flag
    // says to — the flag is opt-out outside development now, and every event is
    // scrubbed in `sentry.ts` before it leaves.
    if (appConfig.isFeatureEnabled('enableErrorReporting')) {
      this.sendErrorToService(errorInfo, additionalInfo);
    } else if (!appConfig.isDevelopment()) {
      // Standing rule 7: if the error is going nowhere, say so once, here,
      // rather than letting it disappear into a buffer with no reader.
      console.error('Monitored Error (error reporting disabled):', errorInfo);
    }
  }

  trackEvent(name: string, data?: unknown): void {
    /*
     * S116's class, found again on 2026-09-11 by the data-practices audit.
     *
     * This printed `data` verbatim, in every build, to a console anyone with the
     * device can open — and the one reachable call site passes `{ userId }` when a
     * user deletes their account (`authService.ts:491` via `Settings.tsx:801`), on
     * the shared classroom Chromebook this product is sold for. `logger` has done the
     * right thing since S116: print in development, `redact()` everywhere else. There
     * is no reason for a second console path that does not.
     */
    logger.debug('Event tracked:', name, data);

    /*
     * LAUNCH_PLAN task 92: consent gates the send, not the banner.
     *
     * `mayTrack('analytics')` is DENIED until a viewer says otherwise, and a COPPA-covered
     * account can never say otherwise (`lockForChildAccount`). The flag is still consulted —
     * it is the operator's switch — but consent is the viewer's, and both have to say yes.
     */
    if (
      appConfig.isProduction() &&
      appConfig.isFeatureEnabled('enableAnalytics') &&
      mayTrack('analytics')
    ) {
      // Example: Google Analytics, Mixpanel, etc.
      // gtag('event', name, data);
    }
  }
  
  recordMetric(name: string, value: number, url?: string): void {
    const metric: PerformanceMetric = {
      name,
      value,
      timestamp: Date.now(),
      url: url || window.location.pathname,
      environment: this.environment
    };

    this.metrics.push(metric);

    // Keep only the most recent metrics
    if (this.metrics.length > this.maxStoredItems) {
      this.metrics.shift();
    }

    // Log significant performance issues
    if (name === 'LCP' && value > 2500) {
      console.warn(`Poor LCP detected: ${value}ms`);
    }
    if (name === 'FID' && value > 100) {
      console.warn(`Poor FID detected: ${value}ms`);
    }
    if (name === 'CLS' && value > 0.1) {
      console.warn(`Poor CLS detected: ${value}`);
    }

    /*
     * Task 92. Performance metrics leave the device for a third party and carry an IP address by
     * virtue of being an HTTP request, so they sit in the `analytics` category with the usage
     * events rather than in a category of their own. `docs/DECISIONS.md` § 11.
     *
     * The local ring buffer above is NOT gated: it never leaves the browser, and the console
     * warnings are how a developer sees a slow page at all.
     */
    if (
      appConfig.isProduction() &&
      appConfig.isFeatureEnabled('enablePerformanceMonitoring') &&
      mayTrack('analytics')
    ) {
      this.sendMetricToService(metric);
    }
  }

  private async sendErrorToService(errorInfo: ErrorInfo, additionalInfo?: Record<string, unknown>): Promise<void> {
    /*
     * Task 92, and this is the branch the decision in `docs/DECISIONS.md` § 11 is actually
     * about. Error telemetry goes to Sentry — a third party, over the network — and a viewer who
     * refuses tracking does not mean "except the crash reporter". So a refusal stops it.
     *
     * What a refusal costs is real and is stated rather than hidden: no crash reports from that
     * viewer. The error still reaches the console, so a developer at the keyboard sees it; what
     * stops is the transmission. Reversing this needs a legitimate-interest assessment, which is
     * counsel's call, not a code change made quietly.
     */
    if (!mayTrack('analytics')) {
      logger.debug('Error not forwarded: the viewer has not consented to telemetry');
      return;
    }

    try {
      // P0.8: forward to Sentry if initialized (initSentry sets window.Sentry).
      // PII scrubbing is handled centrally in src/utils/sentry.ts beforeSend.
      const sentry = (window as unknown as {
        Sentry?: { captureException: (e: Error, ctx?: unknown) => void };
      }).Sentry;
      if (sentry) {
        const err = new Error(errorInfo.message);
        if (errorInfo.stack) err.stack = errorInfo.stack;
        sentry.captureException(err, {
          extra: { ...additionalInfo, componentStack: errorInfo.componentStack, url: errorInfo.url },
        });
      } else {
        // eslint-disable-next-line no-console -- dev fallback when Sentry isn't configured
        console.log('Error (no monitoring service configured):', { errorInfo, additionalInfo });
      }
    } catch (error) {
      console.error('Failed to send error to monitoring service:', error);
    }
  }

  private async sendMetricToService(metric: PerformanceMetric): Promise<void> {
    try {
      // This would integrate with a performance monitoring service
      // eslint-disable-next-line no-console -- logger implementation
      console.log('Would send metric to monitoring service:', metric);
      
      // Example implementation:
      // await fetch('/api/metrics', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(metric)
      // });
    } catch (error) {
      console.error('Failed to send metric to monitoring service:', error);
    }
  }

  getErrors(): ErrorInfo[] {
    return [...this.errors];
  }

  getMetrics(): PerformanceMetric[] {
    return [...this.metrics];
  }

  getMetricsByName(name: string): PerformanceMetric[] {
    return this.metrics.filter(metric => metric.name === name);
  }

  clearErrors(): void {
    this.errors = [];
  }

  clearMetrics(): void {
    this.metrics = [];
  }

  // Memory usage monitoring
  getMemoryUsage(): Record<string, number> | null {
    if ('memory' in performance) {
      const memory = (performance as Performance & {
        memory: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number };
      }).memory;
      return {
        usedJSHeapSize: memory.usedJSHeapSize,
        totalJSHeapSize: memory.totalJSHeapSize,
        jsHeapSizeLimit: memory.jsHeapSizeLimit
      };
    }
    return null;
  }

  // Network information
  getNetworkInfo(): Record<string, unknown> | null {
    if ('connection' in navigator) {
      const connection = (navigator as Navigator & {
        connection: { effectiveType: string; downlink: number; rtt: number; saveData: boolean };
      }).connection;
      return {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt,
        saveData: connection.saveData
      };
    }
    return null;
  }

  // Generate a summary report
  generateReport(): Record<string, unknown> {
    const memoryUsage = this.getMemoryUsage();
    const networkInfo = this.getNetworkInfo();
    const recentErrors = this.errors.slice(-10);
    const recentMetrics = this.metrics.slice(-20);

    return {
      sessionId: this.sessionId,
      environment: this.environment,
      userId: this.userId,
      timestamp: Date.now(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      memoryUsage,
      networkInfo,
      errorCount: this.errors.length,
      recentErrors,
      metricCount: this.metrics.length,
      recentMetrics,
      coreWebVitals: {
        lcp: this.getMetricsByName('LCP').slice(-1)[0]?.value,
        fid: this.getMetricsByName('FID').slice(-1)[0]?.value,
        cls: this.getMetricsByName('CLS').reduce((sum, metric) => sum + metric.value, 0)
      }
    };
  }
}

// Global monitoring instance
export const monitoring = new MonitoringService();

// Export types for external use
export type { ErrorInfo, PerformanceMetric };

// Utility functions for React components
export const withErrorBoundary = (Component: React.ComponentType) => {
  return class ErrorBoundaryWrapper extends React.Component<Record<string, unknown>, { hasError: boolean }> {
    constructor(props: Record<string, unknown>) {
      super(props);
      this.state = { hasError: false };
    }

    static getDerivedStateFromError(_error: Error): { hasError: boolean } {
      return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
      monitoring.logError(error, errorInfo.componentStack || undefined);
    }

    render() {
      if (this.state.hasError) {
        return <div>Something went wrong. The error has been reported.</div>;
      }

      return <Component {...this.props} />;
    }
  };
};

// Hook for monitoring in functional components
export const useMonitoring = () => {
  return {
    logError: monitoring.logError.bind(monitoring),
    recordMetric: monitoring.recordMetric.bind(monitoring),
    getErrors: monitoring.getErrors.bind(monitoring),
    getMetrics: monitoring.getMetrics.bind(monitoring)
  };
};